import 'canvas-toBlob';
import FileSaver from 'file-saver';
import { ContainerSize, ModelCameraOptions, ExportOptions, handleExportOptionType, TrimmingOptions } from '../../types';
import clsx from 'clsx';
import { createTableHTML } from './exportImage';


const buildStyleAttributeValue = (styles: {
  [property: string]: string | number
}) => {
  let styleValue = ''
  for (const property in styles) {
    const value = styles[property]
    if (value !== undefined) {
      styleValue += `${property}:${value};`
    }
  }
  return styleValue
}

/* **************************** 
 * canvasの内容をBlobに変換
 * ****************************/
const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob((blob) => resolve(blob)))


/* **************************** 
 * はみ出すテキストを折り返して表示
 * ****************************/
const drawTextWithWrapping = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number => {
  const words = text.split('');
  let line = '';
  let testLine = '';
  let testWidth = 0;
  let currentY = y;
  let totalHeight = lineHeight;

  for (let n = 0; n < words.length; n++) {
    testLine = line + words[n];
    testWidth = context.measureText(testLine).width;
    if (testWidth > maxWidth && line.length > 0) {
      context.fillText(line, x, currentY);
      line = words[n];
      currentY += lineHeight;
      totalHeight += lineHeight;
    } else {
      line = testLine;
    }
  }
  context.fillText(line, x, currentY);
  return totalHeight;
};

/* ****************************
 * テーブルをCanvasに描画
 * ****************************/
const drawTableOnCanvas = (
  context: CanvasRenderingContext2D,
  tableHTML: string,
  startX: number,
  startY: number,
  tdWidth: number,
  thWidth: number,
  fontSize: number,
  imageHeight: number
) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(tableHTML, 'text/html');
  const table = doc.querySelector('table');
  let currentCol = 1;
  let currentY = startY;
  let currentX = startX;

  if (!table) return;

  context.textBaseline = 'top';
  context.textAlign = 'left';

  const rows = table.querySelectorAll('tr');

  rows.forEach((row) => {
    const cells = row.querySelectorAll('th, td');

    // 各セルのテキストを描画し、高さを取得
    const cellHeights = Array.from(cells).map((cell, colIndex) => {
      const x = currentX + Array.from(cells).slice(0, colIndex).reduce((acc, _, i) => acc + (i === cells.length - 1 ? thWidth : tdWidth), 0);
      const y = currentY;
      let cellHeight = drawTextWithWrapping(context, cell.textContent || '', x + 5, y + 5, (colIndex === cells.length - 1 ? thWidth : tdWidth) - 10, fontSize + 4);

      // cellにテーブルが含まれている場合、高さを調整
      if (cell.querySelector('.table')) {
          cellHeight += 70; // 高さを調整
      }

      return cellHeight;
    });

    // 行の最大高さを取得
    const maxHeight = Math.max(...cellHeights) + 4;

    // 画像の高さを超えそうになったら改行
    if (currentY + maxHeight > imageHeight * currentCol) {
      currentCol++;
      currentY = startY;
      currentX = thWidth + tdWidth + startX;
    }

    cells.forEach((cell, colIndex) => {
      const x = currentX + Array.from(cells).slice(0, colIndex).reduce((acc, _, i) => acc + (i === cells.length - 1 ? thWidth : tdWidth), 0);
      const y = currentY;
  
      // thタグ
      if (cell.tagName.toLowerCase() === 'th') {
          // フォント
          context.font = `bold ${fontSize}px "Helvetica Neue", sans-serif`;
          // セルの背景を描画
          context.fillStyle = '#e6e6fa';
          context.fillRect(x, y, colIndex === cells.length - 1 ? thWidth : tdWidth, maxHeight);
      } else {
          // フォント
          context.font = `${fontSize}px "Helvetica Neue", sans-serif`;
          // セルの背景を描画
          context.fillStyle = '#ffffff';
          context.fillRect(x, y, colIndex === cells.length - 1 ? thWidth : tdWidth, maxHeight);
      }
  
      // セルの境界線を描画
      context.strokeStyle = '#000000';
      context.strokeRect(x, y, colIndex === cells.length - 1 ? thWidth : tdWidth, maxHeight);
  
      // セルのテキストを再描画
      context.fillStyle = '#000000';
  
      // cellの中身が<div>の入れ子構造の場合
      const tableDiv = cell.querySelector('.table');
      if (tableDiv) {
        const headerDiv = tableDiv.querySelector('.header');
        const rowDivs = tableDiv.querySelectorAll('.row');
        const totalRows = rowDivs.length + (headerDiv ? 1 : 0); // ヘッダー行を含めた総行数
        const adjustedMaxHeight = maxHeight / totalRows; // 行数で割った高さ
    
        // 各列の最大幅を計算
        const columnWidths: number[] = [];
        const calculateMaxWidth = (text: string) => {
            context.font = `${fontSize}px "Helvetica Neue", sans-serif`;
            return context.measureText(text).width + 6; // パディングを考慮
        };
    
        if (headerDiv) {
            headerDiv.querySelectorAll('.cell').forEach((headerCell, headerIndex) => {
                const width = calculateMaxWidth(headerCell.textContent ?? '');
                columnWidths[headerIndex] = Math.max(columnWidths[headerIndex] || 0, width);
            });
        }
    
        rowDivs.forEach(rowDiv => {
            rowDiv.querySelectorAll('.cell').forEach((rowCell, cellIndex) => {
                const width = calculateMaxWidth(rowCell.textContent ?? '');
                columnWidths[cellIndex] = Math.max(columnWidths[cellIndex] || 0, width);
            });
        });
    
        if (headerDiv) {
            headerDiv.querySelectorAll('.cell').forEach((headerCell, headerIndex) => {
                const headerX = x + columnWidths.slice(0, headerIndex).reduce((acc, w) => acc + w, 0);
                context.fillText(headerCell.textContent ?? '', headerX + 3, y + 3);
    
                // ヘッダーセルの境界線を描画
                context.strokeStyle = '#000000';
                context.strokeRect(headerX, y, columnWidths[headerIndex], adjustedMaxHeight);
            });
        }
    
        rowDivs.forEach((rowDiv, rowIndex) => {
            rowDiv.querySelectorAll('.cell').forEach((rowCell, cellIndex) => {
                const cellX = x + columnWidths.slice(0, cellIndex).reduce((acc, w) => acc + w, 0);
                const cellY = y + adjustedMaxHeight * (rowIndex + 1); // 行の高さを考慮
    
                context.fillText(rowCell.textContent ?? '', cellX + 3, cellY + 3);
    
                // データセルの境界線を描画
                context.strokeStyle = '#000000';
                context.strokeRect(cellX, cellY, columnWidths[cellIndex], adjustedMaxHeight);
            });
    
            // 行の下に黒い線を引く
            const rowY = y + adjustedMaxHeight * (rowIndex + 1);
            context.strokeStyle = '#000000';
            context.beginPath();
            context.moveTo(x, rowY);
            context.lineTo(x + columnWidths.reduce((acc, w) => acc + w, 0), rowY);
            context.stroke();
        });
      } else {
          drawTextWithWrapping(context, cell.textContent ?? '', x + 5, y + 5, (colIndex === cells.length - 1 ? thWidth : tdWidth) - 10, fontSize + 4);
      }
    });

    currentY += maxHeight;
  });
};

/* ****************************
 * 出展を描画
 * ****************************/
const drawPrintAttributions = (
  context: CanvasRenderingContext2D,
  printAttributions: { [key: string]: string }[] | undefined,
  width: number,
  height: number,
  fontSize: number
) => {
  const y = height - ((fontSize + 5) * (printAttributions?.length || 0) + 10);
  const x = 0;
  let currentY = y + 10;
  let currentX = x;

  context.textBaseline = 'top';
  context.textAlign = 'left';

  // 背景を描画
  context.fillStyle = '#fff';
  context.fillRect(0, y, width, 100);

  printAttributions?.forEach((attribution) => {
    currentX = 10;
    Object.keys(attribution).forEach(key => {
      const text = attribution[key];
      // フォント設定
      context.font = `${fontSize}px Arial`;
      context.fillStyle = 'black';
      // テキストを描画
      context.fillText(text, currentX, currentY);
      const textWidth = context.measureText(text).width;
      currentX += textWidth + 8;
    });
    currentY += fontSize + 5; // 行間を追加
  });

}


/* **************************** 
 * canvasの内容をBlobに変換
 * ****************************/
const cropImage = (
  blob: Blob,
  trimmingOptions: TrimmingOptions,
  attributions: { [key: string]: string } | undefined,
  printAttributions: { [key: string]: string }[] | undefined
): Promise<Blob | null> => {

  const width = trimmingOptions.width * window.devicePixelRatio
  const height = trimmingOptions.height * window.devicePixelRatio
  const left = trimmingOptions.left * window.devicePixelRatio
  const top = trimmingOptions.top * window.devicePixelRatio

  // canvasを取得し、トリミングする
  const trimmingCanvas = document.createElement('canvas')
  const context = trimmingCanvas.getContext('2d')
  trimmingCanvas.width = width
  trimmingCanvas.height = height
  const image = new Image();
  image.src = URL.createObjectURL(blob);
  document.body.append(trimmingCanvas);

  return new Promise((resolve) => {
    image.onload = async () => {
      if (context) {
        context.drawImage(image, left, top, width, height, 0, 0, width, height);
        const fontSize = 12 * window.devicePixelRatio;
        if (attributions) {
          const tableHTML = createTableHTML(attributions);
          const tdWidth = 80;
          const thWidth = 190;
          const startX = 4;
          const startY = 4;

          drawTableOnCanvas(context, tableHTML, startX, startY, tdWidth, thWidth, fontSize, height);
        }
        drawPrintAttributions(context, printAttributions, width, height, fontSize);
      }

      const trimmedBlob = await canvasToBlob(trimmingCanvas);
      trimmingCanvas.remove();
      URL.revokeObjectURL(image.src);
      resolve(trimmedBlob);
    };
  });
}

/* *****************************
 * エクスポート時の設定
 * *****************************/
const handleEachExport = async (
  map: maplibregl.Map,
  containerSize: ContainerSize,
  cameraOptions: ModelCameraOptions | null = null,
  withAttributions?: boolean,
  attributionText?: { [key: string]: string } | undefined,
  printAttributions?: { [key: string]: string }[] | undefined
) => {

  if (cameraOptions) {
    map.setCenter(cameraOptions.center)
    map.setZoom(cameraOptions.zoom)
    map.setBearing(cameraOptions.bearing)
    map.setPitch(cameraOptions.pitch)
  }

  const canvas = map.getCanvas();
  const trimmingOptions: TrimmingOptions = {
    width: containerSize.outerWidth - containerSize.margin.horizontal * 2,
    height: containerSize.outerHeight - containerSize.margin.vertical * 2,
    left: containerSize.margin.horizontal,
    top: containerSize.margin.vertical,
  }
  
  let blob = await canvasToBlob(canvas);
  
  if (blob) {
    // トリミングする
    blob = await cropImage(
      blob,
      trimmingOptions,
      withAttributions ? attributionText : undefined,
      printAttributions
    )
  }
  const { width, height } = canvas
  return { blob, width, height, attributionText }
}



/* ************ png書き出し時 ************ */

class ExportControl {
  private loadingBackground: HTMLDivElement
  private loadingContainer: HTMLDivElement
  private loadingProgressText: HTMLDivElement
  private actualPixelRatio: number = window.devicePixelRatio;
  // 出力元の map
  private mapStyle: maplibregl.StyleSpecification;
  private mapZoom: number;
  private mapPitch: number;
  private bearing: number;
  private printAttributions: { [key: string]: string; }[] | undefined

  private featureGeojson: maplibregl.MapGeoJSONFeature | undefined;


  // 出力に関わるパラメータ
  private containerSize: ContainerSize
  private cameraOptions: ModelCameraOptions

  constructor(
    map: maplibregl.Map,
    exportOptions: {
      containerSize: ContainerSize
      cameraOptions: ModelCameraOptions
    },
    geojsonFeature: maplibregl.MapGeoJSONFeature | undefined,
    printAttributions: { [key: string]: string; }[] | undefined
  ) {
    const currentExportOptions: Omit<ExportOptions, 'aspectRatio' | 'mapRect'> = exportOptions;
    this.mapStyle = JSON.parse(JSON.stringify(map.getStyle())) as maplibregl.StyleSpecification;
    this.mapZoom = map.getZoom();
    this.mapPitch = map.getPitch();
    this.bearing = map.getBearing();
    this.featureGeojson = geojsonFeature;
    this.printAttributions = printAttributions;

    // queryRenderedFeatures を使って、実際に地物が含まれているレイヤーを判別する
    const renderedFeatures = map.queryRenderedFeatures();

    const rendered_vector_layers = new Set<string>();
    
    for (const feature of renderedFeatures) {
      rendered_vector_layers.add(feature.layer.id);
    }
    const { containerSize, cameraOptions } = currentExportOptions;
    this.containerSize = containerSize;
    this.cameraOptions = cameraOptions;

    // Init loader
    this.loadingBackground = document.createElement('div')
    this.loadingBackground.className = clsx(
      'fixed inset-0 z-[9998] grid place-items-center bg-black/60'
    )
    this.loadingContainer = document.createElement('div')
    this.loadingBackground.appendChild(this.loadingContainer)
    this.loadingProgressText = document.createElement('div')
    document.body.append(this.loadingBackground)
  }

  public destructor() {
    Object.defineProperty(window, 'devicePixelRatio', {
      get: () => this.actualPixelRatio,
    })
    this.loadingBackground?.remove()
    this.loadingContainer?.remove()
    this.loadingProgressText?.remove()
  }

  
  /* ****************
   * 指定範囲で一時的な地図を作成
   * ****************/
  private prepareExport = async (exportRange: { width: number; }): Promise<maplibregl.Map> => {
    const { width } = exportRange;
    const { outerWidth, outerHeight, margin } = this.containerSize;

    const tmpMapContainer = document.createElement('div');
    document.body.appendChild(tmpMapContainer)

    tmpMapContainer.setAttribute(
      'style',
      buildStyleAttributeValue({
        visibility: 'hidden',
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: `${outerWidth}px`,
        height: `${outerHeight}px`,
      })
    )
    tmpMapContainer.setAttribute('class', 'map');

    const exportDevicePixelRatio = width / (outerWidth - 2 * margin.horizontal);
    Object.defineProperty(window, 'devicePixelRatio', {
      get: () => exportDevicePixelRatio,
    });

    const tmpMap = new window.geolonia.Map({
      container: tmpMapContainer,
      style: this.mapStyle,
      localIdeographFontFamily: undefined,
      hash: false,
      interactive: false,
      attributionControl: false,
      canvasContextAttributes: {
        preserveDrawingBuffer: true
      },
      center: this.cameraOptions.center,
      zoom: this.mapZoom,
      pitch: this.mapPitch,
      bearing: this.bearing,
    });

    // 存在しない画像があった場合追加
    tmpMap.on('styleimagemissing', (e) => {
      const spriteId = e.id;
      const url = `${location.protocol}//${location.host}${location.pathname}sprites/${spriteId}.png`;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      tmpMap.loadImage(url, (error: anyType, image: anyType) => {
        if (error) throw error;
        if (image && !tmpMap.hasImage(spriteId)) {
          tmpMap.addImage(spriteId, image);
        }
      });
    });

    // アイドル状態になるまで待つ（リソースの読み込み、レンダリング完了状態）
    const tmpMapIdlePromise = tmpMap.once('idle');
    await tmpMapIdlePromise;

    return tmpMap;
  }

  /* ****************
   * 地図オブジェクトとコンテナのクリーンアップ
   * ****************/
  private cleanupExport = (tmpMap: maplibregl.Map) => {
    const tmpMapContainer = tmpMap.getContainer();
    if (tmpMapContainer.parentNode) {
      tmpMapContainer.parentNode.removeChild(tmpMapContainer);
    }
    tmpMap.remove();
    tmpMapContainer.remove();
  }

  /**
   * 新しい Map インスタンスを作成してスタイルをコピーし、Canvas から画像を出力する
   * @param mapFileName 名前
   * @param exportRange 出力する大きさ
   * @param options png モード: 統合レイヤーだけを出力
   * @returns Blob | null サムネイル画像の Blob
   */
  public handleExport = async (mapFileName: string, exportRange: { width: number; height: number }, options: handleExportOptionType) => {
    const { download, thumbHeight } = options;

    let thumbnailBlob: Blob | null = null
    const targetBlob: Blob | null = null

    try {
      const generateThumbnail = async ({ height = 180, withAttributions }: { height?: number, withAttributions?: boolean }) => {
        const width = (height * exportRange.width) / exportRange.height;
        const tmpMap = await this.prepareExport({ width });

        // 諸々の設定を行い、画像を生成
        const { blob } = await handleEachExport(
          tmpMap,
          this.containerSize,
          this.cameraOptions,
          withAttributions,
          this.featureGeojson?.properties,
          this.printAttributions
        );
        this.cleanupExport(tmpMap);

        return blob;
      }

      // サムネイル画像の生成
      thumbnailBlob = await generateThumbnail({
        height: thumbHeight,
        withAttributions: true,
      });

      // pngで保存
      if (thumbnailBlob && download) {
        FileSaver.saveAs(thumbnailBlob, `${mapFileName}.png`);
      }
    } catch (error) {
      console.error(error);
    }

    this.destructor()
    return { thumbnailBlob, targetBlob }
  }
}

export default ExportControl;
